import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v1 as uuidv1 } from "uuid";
import { useMutation } from "react-query";

import { getFileErrors } from "../helpers/files";
import { sizeConverter } from "../helpers/sizeConverter";
import { INVALID_FILE_ERROR_MESSAGE } from "../helpers/errorMessages";

import {
  FileInterface,
  FileStatus,
  Progress,
  FileRejection,
} from "../types/common";
import { UploadFileParams } from "../components/FileUploader/modules/types";

interface Props {
  onChange: (updatedValue: FileInterface[], isValidated?: boolean) => void;
  path: string;
  files: FileInterface[];
  maxSize?: number;
  maxFiles?: number;
  externalError?: string;
  externalBannerTitle?: string;
  externalHintText?: string;
  uploadFile: (params: UploadFileParams) => Promise<Partial<FileInterface>>;
  acceptMimeTypes: string[];
}

// 200 Mb
const MAX_FILES_OVERALL_SIZE = 209715200;

const useFileUploader = ({
  onChange,
  path,
  files,
  maxSize = MAX_FILES_OVERALL_SIZE,
  maxFiles = 50,
  externalError,
  externalBannerTitle = "uploader.bannerTitle",
  externalHintText = "uploader.hintText",
  uploadFile,
  acceptMimeTypes,
}: Props) => {
  const [uploadProgressData, setUploadProgressData] = useState<Progress>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [bannerTitle, setBannerTitle] = useState("");
  const [isErrorDismiss, setErrorDismiss] = useState(false);
  const { t } = useTranslation();
  const filesRef = useRef(files);

  const overallSize = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files]
  );

  const isMaxSizeReached = overallSize >= maxSize;
  const isMaxFilesReached = !!maxFiles && files.length >= maxFiles;
  const disabled = isMaxSizeReached || isMaxFilesReached;

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    if (externalError && externalError !== INVALID_FILE_ERROR_MESSAGE) {
      setErrors([externalError]);
      setBannerTitle(externalBannerTitle);
      setErrorDismiss(false);
    }
  }, [externalError, externalBannerTitle]);

  const [fileUpload] = useMutation(uploadFile, {
    onMutate: (data) => {
      const updatedData = [
        ...filesRef.current,
        {
          id: data.tempId,
          description: "",
          status: FileStatus.Loading,
          name: data.file.name,
          size: data.file.size,
        },
      ];

      onChange(updatedData);
    },
    onSuccess: (data: Partial<FileInterface>, variables: UploadFileParams) => {
      const updatedData = filesRef.current.map((file) => {
        return file.id === variables.tempId
          ? { ...file, ...data, status: FileStatus.Loaded }
          : file;
      });

      onChange(updatedData, true);
    },
    onError: (_, { tempId }) => {
      const updatedData = filesRef.current.map((file) => {
        return file.id === tempId
          ? {
              ...file,
              status: FileStatus.Added,
              isError: true,
              errorText: t("errors.fileUploadError"),
            }
          : file;
      });

      onChange(updatedData, true);
    },
  });

  const checkFilesDuplicates: (
    currentFile: File,
    round?: number
  ) => File = useCallback(
    (currentFile, round = 0) => {
      const duplicates = files.filter(
        (item) =>
          item.name === currentFile.name && item.size === currentFile.size
      );
      const duplicatesCount = duplicates.length;

      if (duplicatesCount) {
        const lastDot = currentFile.name.lastIndexOf(".");
        const fileName = currentFile.name.substring(0, lastDot);
        const fileNameUpdated =
          round > 0 ? fileName.replace(/(\(\d+\))+/, "") : fileName;
        const ext = currentFile.name.substring(lastDot + 1);
        const newFile = new File(
          [currentFile],
          `${fileNameUpdated}(${duplicatesCount + round}).${ext}`,
          {
            type: currentFile.type,
          }
        );

        return checkFilesDuplicates(newFile, round + 1);
      }

      return currentFile;
    },
    [files]
  );

  const verifyFiles = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      let allowedSize = MAX_FILES_OVERALL_SIZE - overallSize;
      const acceptedFilesArray: File[] = [];
      let filesSizeReachedError = "";

      acceptedFiles.forEach((item) => {
        const newAllowedSize = allowedSize - item.size;

        if (newAllowedSize >= 0) {
          allowedSize = newAllowedSize;
          const file = checkFilesDuplicates(item);

          acceptedFilesArray.push(file);
        } else {
          filesSizeReachedError = "file-too-large";
        }
      });

      const fileErrors = getFileErrors(rejectedFiles, filesSizeReachedError, t);
      const newBannerTitle = fileErrors.length
        ? "uploader.bannerFileErrorTitle"
        : "";

      setErrors(fileErrors);
      setErrorDismiss(Boolean(fileErrors.length));
      setBannerTitle(newBannerTitle);

      return acceptedFilesArray;
    },
    [checkFilesDuplicates, overallSize, t]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const acceptedFilesArray = verifyFiles(acceptedFiles, rejectedFiles);

      if (acceptedFilesArray.length) {
        acceptedFilesArray.forEach((item) => {
          // @ts-ignore It's not required in this use-case
          fileUpload({
            path,
            tempId: uuidv1(),
            file: item,
            cb: setUploadProgressData,
          });
        });
      }
    },
    [fileUpload, path, verifyFiles]
  );

  const onErrorDismiss = () => {
    const newErrors = externalError ? [externalError] : [];
    const newBannerTitle = newErrors.length ? externalBannerTitle : "";

    setErrors(newErrors);
    setBannerTitle(newBannerTitle);
    setErrorDismiss(false);
  };

  const hintText = (() => {
    if (isMaxSizeReached) {
      return t("uploader.filesSizeReached", { size: sizeConverter(maxSize) });
    }
    if (isMaxFilesReached) {
      return t("uploader.filesCountReached", { count: maxFiles });
    }
    return t(externalHintText, { size: sizeConverter(maxSize) });
  })();

  return {
    files,
    filesRef,
    disabled,
    errors,
    acceptMimeTypes,
    bannerTitle,
    isErrorDismiss,
    onErrorDismiss,
    hintText,
    onDrop,
    uploadProgressData,
    maxSize,
    maxFiles: maxFiles ? maxFiles - files.length : undefined,
  };
};

export default useFileUploader;
